import {AddEditCommentPanel} from '../note-detail/add-edit-comment-panel';
import NoteApprove from '../note-detail/note-approve';
import {NoteDetailComments} from '../note-detail/note-detail-comments';
import {NoteDetailContent} from '../note-detail/note-detail-content';
import {NoteDetailForm} from '../note-detail/note-detail-form';
import {NoteDetailHeader} from '../note-detail/note-detail-header';
import {View} from '@/components/react-native';
import SidePanel from '@/components/side-panel';
import {
  createNoteForAirportNote,
  deleteNoteForAirportNote,
  updateNoteForAirportNote
} from '@/firebase/airport-note';
import useAirportNote from '@/stores/airport-note';
import {ENoteTag, INoteInfo} from '@/types/airport';
import moment from 'moment';
import {useCallback, useEffect, useState} from 'react';

const AirportNoteSelected = () => {
  const airportNoteInfo = useAirportNote((state) => state.airportNoteInfo);
  const selectedNote = useAirportNote((state) => state.selectedNote);
  const setSelectedNote = useAirportNote((state) => state.setSelectedNote);
  const isEditNote = useAirportNote((state) => state.isEditNote);
  const setIsEditNote = useAirportNote((state) => state.setIsEditNote);
  const [noteStatus, setNoteStatus] = useState<
    'pending' | 'approved' | 'rejected'
  >('pending');
  const [noteContent, setNoteContent] = useState<string>(
    selectedNote?.content || ''
  );
  const sidePanelVisible = useAirportNote(
    (state) => state.noteSidePanelVisible
  );
  const setSidePanelVisible = useAirportNote(
    (state) => state.setNoteSidePanelVisible
  );

  const handleCloseSidePanel = useCallback(() => {
    if (isEditNote) {
      setIsEditNote(false);
    }
    setNoteStatus('pending');
    setSidePanelVisible(false);
    setTimeout(() => {
      setSelectedNote(null);
    }, 300);
  }, [isEditNote]);

  const handleSaveNote = useCallback(
    async (content: string, summary: string, tags: ENoteTag[]) => {
      if (!airportNoteInfo?.icao_code || !selectedNote) return;

      try {
        if (selectedNote.id === 'new-note') {
          // Create new note
          const noteData: Omit<INoteInfo, 'id'> = {
            airport: airportNoteInfo.icao_code,
            tags,
            category: selectedNote.category,
            content,
            summary: summary || '',
            is_new: false,
            created_by: 'Admin',
            created_at: moment().format('YYYY-MM-DD HH:mm:ss'),
            approved_by: 'Admin',
            approved_at: moment().format('YYYY-MM-DD HH:mm:ss'),
            comment_count: 0,
            user_id: ''
          };

          await createNoteForAirportNote(airportNoteInfo.icao_code, noteData);
          console.log('New note created successfully');
        } else {
          // Update existing note
          const updateData: Partial<INoteInfo> = {
            content,
            summary: summary || selectedNote.summary || '',
            tags
          };

          await updateNoteForAirportNote(
            airportNoteInfo.icao_code,
            selectedNote.id,
            updateData
          );
          console.log('Note updated successfully');
        }

        // Close the side panel
        setSidePanelVisible(false);
        setTimeout(() => {
          setSelectedNote(null);
        }, 300);

        if (isEditNote) {
          setIsEditNote(false);
        }
      } catch (error) {
        console.error('Error saving note:', error);
        // TODO: Show error toast to user
      }
    },
    [airportNoteInfo, selectedNote, isEditNote]
  );

  const handleDeleteNote = useCallback(async () => {
    if (
      !airportNoteInfo?.icao_code ||
      !selectedNote ||
      selectedNote.id === 'new-note'
    )
      return;

    try {
      await deleteNoteForAirportNote(
        airportNoteInfo.icao_code,
        selectedNote.id
      );
      console.log('Note deleted successfully');

      setSidePanelVisible(false);
      setTimeout(() => {
        setSelectedNote(null);
      }, 300);
      setIsEditNote(false);
    } catch (error) {
      console.error('Error deleting note:', error);
      // TODO: Show error toast to user
    }
  }, [airportNoteInfo, selectedNote]);

  const handleUpdateNoteStatus = useCallback(async () => {
    if (!selectedNote || !airportNoteInfo?.icao_code) return;
    if (noteStatus === 'approved') {
      await updateNoteForAirportNote(
        airportNoteInfo.icao_code,
        selectedNote.id,
        {
          is_new: true,
          content: noteContent,
          approved_by: 'Admin',
          approved_at: moment().format('YYYY-MM-DD HH:mm:ss')
        }
      );
    } else if (noteStatus === 'rejected') {
      await updateNoteForAirportNote(
        airportNoteInfo.icao_code,
        selectedNote.id,
        {
          rejected_at: moment().format('YYYY-MM-DD HH:mm:ss'),
          rejected_by: 'Admin'
        }
      );
    }
    handleCloseSidePanel();
  }, [selectedNote, noteStatus, noteContent]);

  useEffect(() => {
    if (selectedNote) {
      setNoteContent(selectedNote.content);
    }
  }, [selectedNote]);

  if (!selectedNote) return null;
  return (
    <View className="absolute top-0 left-0 right-0 bottom-0">
      <SidePanel
        visible={sidePanelVisible}
        onClose={handleCloseSidePanel}
        width={440}
      >
        <View
          className="bg-[#090024] flex-1 pt-[6px] overflow-auto"
          style={{
            height: window.innerHeight - 108
          }}
        >
          {selectedNote.id === 'new-note' || isEditNote ? (
            /* Show form for creating new note - form has its own header */
            <NoteDetailForm
              id={selectedNote.id}
              initialContent={selectedNote.content}
              initialTags={selectedNote.tags}
              category={selectedNote.category}
              airportName={airportNoteInfo?.iata_code || '[Airport Name]'}
              airportCode={`${airportNoteInfo?.iata_code || '[IATA]'}/${
                airportNoteInfo?.icao_code || '[ICAO]'
              }`}
              noteData={selectedNote}
              onSave={handleSaveNote}
              onCancel={handleCloseSidePanel}
              onDelete={handleDeleteNote}
            />
          ) : (
            /* Show existing note content and comments */
            <>
              <NoteDetailHeader
                item={selectedNote}
                onClose={handleCloseSidePanel}
                onSave={handleUpdateNoteStatus}
              />
              <div className="flex-grow flex-1 overflow-auto">
                <NoteDetailContent item={selectedNote} />
                {selectedNote?.approved_by ? (
                  <NoteDetailComments
                    noteId={selectedNote.id}
                    noteItem={selectedNote}
                  />
                ) : (
                  <NoteApprove
                    status={noteStatus}
                    setStatus={setNoteStatus}
                    content={noteContent}
                    setContent={setNoteContent}
                  />
                )}
              </div>
            </>
          )}
        </View>
      </SidePanel>
      {selectedNote && <AddEditCommentPanel noteItem={selectedNote} />}
    </View>
  );
};

export default AirportNoteSelected;
